// Titles on touch (R1555, ui-map-interface.md §8 "Titles on touch").
//
// About sixty controls of the chrome carry a `title`, and a browser shows a
// title on a hover alone. A finger has no hover, so the title said nothing
// on a telephone. A still finger that rests `TITLE_HOLD_MS` on an element
// with a title now shows the title in the explainer card `#bartip`.
//
// `TitleHold` of `ui/touch.ts` decides; this module wires it to the DOM. It
// listens on `#ui` alone, in the capture phase, and on touch alone. The map
// canvas is not a child of `#ui`, so the map's own long press (the peek)
// and its pinch never reach this module, and a mouse keeps its hover.

import { TITLE_HOLD_MS } from '../gen/presentation';
import { TitleHold } from './touch';

/** What the overlay keeps of the wiring. */
export interface TitleHoldWiring {
  /** True from a hold until the next press comes down. The release of a
   *  held press must not act, so the overlay's release hosts ask it. */
  held(): boolean;
  /** Takes the listeners and the timer off. */
  off(): void;
}

/**
 * The elements whose title a hold leaves alone. A top-bar cell opens its
 * own explainer card on the press, and that card states the title (§12.0).
 * A term button opens its definition on a tap, and the card itself is
 * already the explainer (R1557). The chat log is a document, so its long
 * press is the text callout (R238). A field takes the caret. The long press
 * of the VIEW button opens the quick wheel (R1610, R1747), so its title
 * would stand under the wheel.
 */
const SKIP = '[data-tip], button.term, #bartip, #chatlog, input, textarea, select, button[data-act="view"]';

/** The title a press on `target` asks for, or null. */
export function titleOf(target: EventTarget | null, root: Element): string | null {
  if (!(target instanceof Element)) return null;
  const el = target.closest('[title]');
  if (!el || !root.contains(el) || el.closest(SKIP)) return null;
  const text = (el.getAttribute('title') ?? '').trim();
  return text === '' ? null : text;
}

/**
 * Wires the title hold on `root`. `show` gets the title and the press
 * point in client px; the overlay draws the card there.
 */
export function installTitleHold(
  root: HTMLElement, show: (text: string, x: number, y: number) => void,
): TitleHoldWiring {
  const hold = new TitleHold<string>(TITLE_HOLD_MS);
  let timer: ReturnType<typeof setTimeout> | null = null;
  // The event clock of the lift of the last held press. The click that
  // the browser sends after that lift is the hold's own, and no press.
  let heldLift: number | null = null;

  const stop = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  // A press that never moves raises no further events, so the hold needs
  // a timer. The timer reads the wall clock, which can fire the hold late
  // and never early; a timer that ran a little short arms again for the
  // rest (R583).
  const arm = () => {
    const due = hold.dueIn(performance.now());
    if (due === null) return;
    timer = setTimeout(() => {
      timer = null;
      hold.tick(performance.now());
      arm();
    }, due);
  };
  hold.onHold = (text, x, y) => {
    stop();
    show(text, x, y);
  };

  const down = (ev: PointerEvent) => {
    if (ev.pointerType !== 'touch') return;
    stop();
    heldLift = null;
    hold.down(ev.pointerId, ev.clientX, ev.clientY, ev.timeStamp, titleOf(ev.target, root));
    arm();
  };
  const move = (ev: PointerEvent) => {
    if (ev.pointerType !== 'touch') return;
    hold.move(ev.pointerId, ev.clientX, ev.clientY);
    if (!hold.pending) stop();
  };
  const up = (ev: PointerEvent) => {
    if (ev.pointerType !== 'touch') return;
    stop();
    hold.up(ev.pointerId, ev.timeStamp);
    if (hold.held) heldLift = ev.timeStamp;
  };
  const cancel = (ev: PointerEvent) => {
    if (ev.pointerType !== 'touch') return;
    stop();
    hold.cancel(ev.pointerId);
  };
  // The click a browser sends after the lift of a hold must not press the
  // control. One hold time is the window: the click follows its lift at
  // once, and a later click is a new press.
  const click = (ev: MouseEvent) => {
    if (heldLift === null || ev.timeStamp - heldLift > TITLE_HOLD_MS) return;
    heldLift = null;
    ev.preventDefault();
    ev.stopPropagation();
  };
  // An Android browser raises `contextmenu` from its own long press, and
  // it may take the touch away after it. The platform has said "this is
  // a long press", so the waiting hold fires now and the menu stays shut.
  const menu = (ev: Event) => {
    if (!hold.pending) return;
    ev.preventDefault();
    hold.fire();
  };
  // A hidden page never hears the lift, so every later press would count
  // as a second finger.
  const hide = () => {
    if (!document.hidden) return;
    stop();
    hold.reset();
  };

  root.addEventListener('pointerdown', down, true);
  root.addEventListener('pointermove', move, true);
  root.addEventListener('pointerup', up, true);
  root.addEventListener('pointercancel', cancel, true);
  root.addEventListener('click', click, true);
  root.addEventListener('contextmenu', menu, true);
  document.addEventListener('visibilitychange', hide);

  return {
    held: () => hold.held,
    off: () => {
      stop();
      root.removeEventListener('pointerdown', down, true);
      root.removeEventListener('pointermove', move, true);
      root.removeEventListener('pointerup', up, true);
      root.removeEventListener('pointercancel', cancel, true);
      root.removeEventListener('click', click, true);
      root.removeEventListener('contextmenu', menu, true);
      document.removeEventListener('visibilitychange', hide);
    },
  };
}
